import type {
  MatterContactConnection,
  Contact,
  Matter,
  MatterDocumentSubItem,
} from "@/lib/types";
import { getActiveContactConnections } from "@/lib/utils/connections/item-contact-connections";
import { getMatterContactConnections } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import type { ContactConnectionWithContact } from "./templateDictionaryOptions";

const settlementContactRoles = {
  Client: "client",
  Defendant: "defendant",
  Payee: "payee",
  "Primary Contact": "primary_contact",
  Other: "other",
} as const;

const settlementSources = [
  {
    collection: "settlementOffers",
    sourceType: "matter_settlement_offer",
    subItemType: "settlement-offer",
    linkedId: "settlementOfferId",
  },
  {
    collection: "settlementDemands",
    sourceType: "matter_settlement_demand",
    subItemType: "settlement-demand",
    linkedId: "settlementDemandId",
  },
  {
    collection: "settlementCounters",
    sourceType: "matter_settlement_counter",
    subItemType: "settlement-counter",
    linkedId: "settlementCounterId",
  },
] as const;

type SettlementMatter = Pick<
  Matter,
  "settlementOffers" | "settlementDemands" | "settlementCounters"
>;

// Presentation adapters can return a connection before its contact is hydrated.
type ProjectedContactConnection = Omit<MatterContactConnection, "contact"> & {
  contact?: Contact | null;
};

export const isSettlementNegotiationContactConnection = (
  connection: Pick<
    MatterContactConnection,
    "settlementOfferId" | "settlementDemandId" | "settlementCounterId"
  >,
) => settlementSources.some(({ linkedId }) => Boolean(connection[linkedId]));

const getSettlementContactSources = (
  matter: SettlementMatter,
  subItem?: MatterDocumentSubItem,
) => {
  const selectedSource = settlementSources.find(
    ({ subItemType }) => subItemType === subItem?.type,
  );
  return settlementSources.flatMap((source) =>
    selectedSource && source !== selectedSource
      ? []
      : matter[source.collection]
          .filter((item) => !selectedSource || item.id === subItem?.id)
          .map((item) => ({ ...source, item })),
  );
};

const getSettlementContactConnections = (
  sources: ReturnType<typeof getSettlementContactSources>,
) =>
  sources.flatMap(({ item, sourceType }) =>
    Object.entries(settlementContactRoles).flatMap(([label, role]) =>
      [`${sourceType}_to_${role}`, `${sourceType}_to_legacy_${role}`].flatMap(
        (connectionType) =>
          getActiveContactConnections(item, sourceType, connectionType).map(
            (connection) => ({ connection, label }),
          ),
      ),
    ),
  );

export const getSettlementNegotiationContactIds = (
  matter: SettlementMatter,
): string[] =>
  getSettlementContactConnections(getSettlementContactSources(matter)).map(
    ({ connection }) => connection.targetObjectId,
  );

export const buildSettlementNegotiationContactConnections = (
  matter: Matter,
  contacts: Contact[],
  subItem?: MatterDocumentSubItem,
): ContactConnectionWithContact[] => {
  const sources = getSettlementContactSources(matter, subItem);
  const canonicalConnections = getSettlementContactConnections(sources);
  const canonicalSourceIds = new Set(
    canonicalConnections.map(({ connection }) =>
      JSON.stringify([connection.sourceObjectType, connection.sourceObjectId]),
    ),
  );
  const matterConnections = getMatterContactConnections(matter);
  // Multiple links to one profile and role should produce one choice.
  const seen = new Set(
    matterConnections
      .filter(
        (connection) => !isSettlementNegotiationContactConnection(connection),
      )
      .map((connection) =>
        JSON.stringify([connection.contactId, connection.label]),
      ),
  );
  const canonical = canonicalConnections.flatMap(({ connection, label }) => {
    const contact = contacts.find(
      (candidate) => candidate.id === connection.targetObjectId,
    );
    const key = JSON.stringify([connection.targetObjectId, label]);
    if (!contact || seen.has(key)) return [];
    seen.add(key);
    return [{ contact, contactId: contact.id, id: connection.id, label }];
  });
  const projected = matterConnections.flatMap(
    (connection: ProjectedContactConnection) => {
      const source = sources.find(
        ({ item, linkedId }) => connection[linkedId] === item.id,
      );
      if (
        !source ||
        canonicalSourceIds.has(
          JSON.stringify([source.sourceType, source.item.id]),
        )
      ) {
        return [];
      }
      const contact =
        contacts.find((candidate) => candidate.id === connection.contactId) ??
        connection.contact;
      const key = JSON.stringify([connection.contactId, connection.label]);
      if (!contact || seen.has(key)) return [];
      seen.add(key);
      return [{ ...connection, contact }];
    },
  );
  return [...canonical, ...projected];
};
